import { QueryStatus, useQuery } from "@tanstack/react-query";
import { ChartLine } from "@phosphor-icons/react";
import { useReactTable, getCoreRowModel, type ColumnDef, flexRender } from "@tanstack/react-table";

import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";

import { cn, message } from "@/lib/utils";
import { ConsumerResearchApiFactory } from "@/apis/brand-os/consumer-research";
import type { ChannelHealthItem } from "@/schema/brand-os/ai-consumer-research";

type ChannelStatus = "excellent" | "good" | "strong" | "growing" | "stable" | "declining" | "at-risk";

const badgeClasses: Record<ChannelStatus, string> = {
  excellent: "bg-emerald-100 text-emerald-700",
  good: "bg-cyan-100 text-cyan-700",
  strong: "bg-emerald-100 text-emerald-700",
  growing: "bg-blue-200 text-blue-900",
  stable: "bg-gray-200 text-gray-600",
  declining: "bg-amber-200 text-amber-900",
  "at-risk": "bg-rose-200 text-rose-700",
};

const colsWidth = ["w-[18%]", "w-[12%]", "w-[18%]", "w-[12%]", "w-[12%]", "w-[12%]", "w-[16%]"] as const;
function deriveStatus(sentiment: number): ChannelStatus {
  switch (true) {
    case sentiment > 90:
      return "excellent";
    case sentiment > 80:
      return "good";
    case sentiment > 70:
      return "strong";
    case sentiment > 60:
      return "growing";
    case sentiment > 50:
      return "stable";
    case sentiment > 40:
      return "declining";
    default:
      return "at-risk";
  }
}

function formatVolume(volume: number): string {
  if (volume >= 1_000_000) return `${(volume / 1_000_000).toFixed(1)}M`;
  if (volume >= 1_000) return `${(volume / 1_000).toFixed(1)}K`;
  return String(volume);
}

function sentimentBarColor(sentiment: number): string {
  if (sentiment >= 65) return "bg-emerald-500";
  if (sentiment >= 50) return "bg-amber-500";
  return "bg-destructive";
}

const channelColumns: ColumnDef<ChannelHealthItem>[] = [
  {
    accessorKey: "channel_name",
    header: "Channel",
    cell: ({ row }) => <span className="font-medium text-foreground capitalize">{row.original.channel_name}</span>,
  },
  {
    accessorKey: "volume",
    header: () => <span className="flex justify-center">Volume</span>,
    cell: ({ row }) => <div className="text-center text-sm text-muted-foreground font-semibold">{formatVolume(row.original.volume)}</div>,
  },
  {
    accessorKey: "sentiment",
    header: () => <span className="flex justify-center">Sentiment</span>,
    cell: ({ row }) => {
      const { sentiment } = row.original;
      return (
        <div className="flex items-center justify-center gap-2 min-w-24">
          <Progress value={sentiment} className="h-2 w-14 bg-muted shrink-0" indicatorClassName={sentimentBarColor(sentiment)} />
          <span className="text-sm  w-8">{sentiment}</span>
        </div>
      );
    },
  },
  {
    accessorKey: "growth",
    header: () => <span className="flex justify-center">Growth</span>,
    cell: ({ row }) => {
      const { growth } = row.original;
      if (growth == null) return <div className="text-center text-sm text-muted-foreground">—</div>;
      const isPositive = growth >= 0;
      return (
        <div className={cn("text-center text-sm font-medium", isPositive ? "text-success" : "text-destructive")}>
          {isPositive ? "+" : ""}
          {growth}
        </div>
      );
    },
  },
  {
    accessorKey: "response_time",
    header: () => <span className="flex justify-center">Resp. Time</span>,
    cell: ({ row }) => <div className="text-center text-sm text-muted-foreground">{row.original.response_time == null ? "—" : `${row.original.response_time}h`}</div>,
  },
  {
    accessorKey: "engagement",
    header: () => <span className="flex justify-center">Engage</span>,
    cell: ({ row }) => <div className="text-center text-sm text-muted-foreground">{row.original.engagement_percentage == null ? "—" : `${row.original.engagement_percentage} %`}</div>,
  },
  {
    accessorKey: "status",
    header: () => <span className="flex justify-center">Status</span>,
    cell: ({ row }) => {
      const status = deriveStatus(row.original.sentiment);
      return (
        <div className="flex justify-center">
          <Badge className={cn("border-transparent rounded-full w-28 justify-center capitalize", badgeClasses[status])} variant="secondary">
            {status}
          </Badge>
        </div>
      );
    },
  },
];

function ChannelHealthComponent({ status, items }: { status: QueryStatus; items: ChannelHealthItem[] }) {
  switch (status) {
    case "pending":
      return <ChannelHealthSkeleton />;
    case "error":
      return <LockedContent message={message} />;
    case "success":
      return items.length ? <ChannelHealthTable items={items} /> : <LockedContent message={message} />;
  }
}

export function ChannelHealth() {
  const { data, status } = useQuery(ConsumerResearchApiFactory.Queries.ChannelHealth());

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span className="flex size-9 items-center justify-center rounded-lg bg-muted">
            <ChartLine className="size-5 text-muted-foreground" weight="bold" />
          </span>
          Channel Health
        </CardTitle>
        <CardDescription className="pl-11">Performance metrics per platform</CardDescription>
        <CardAction>
          <span className="text-xs text-muted-foreground">Live</span>
        </CardAction>
      </CardHeader>
      <CardContent>
        <ChannelHealthComponent status={status} items={data?.channels ?? []} />
      </CardContent>
    </Card>
  );
}

function ChannelHealthTable({ items }: { items: ChannelHealthItem[] }) {
  const columns = channelColumns;
  const table = useReactTable({ data: items, columns, getCoreRowModel: getCoreRowModel() });

  return (
    <div className="overflow-hidden rounded-lg border">
      <div className="max-h-96 overflow-y-auto scrollbar-hidden">
        <table className="w-full caption-bottom text-sm">
          <thead>
            {table.getHeaderGroups().map((header_group) => (
              <tr key={header_group.id} className="border-b bg-background">
                {header_group.headers.map((header, i) => (
                  <th key={header.id} className={cn("sticky top-0 z-20 bg-background h-12 px-4 font-medium text-muted-foreground", colsWidth[i], i === 0 ? "text-left" : "text-center")}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="border-b">
                {row.getVisibleCells().map((cell, i) => (
                  <td key={cell.id} className={cn("p-4", colsWidth[i], i === 0 ? "text-left" : "text-center")}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ChannelHealthSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 6 }, (_, i) => (
        <Skeleton key={i} className="h-8 w-full rounded" />
      ))}
    </div>
  );
}
